/**
 * API client — typed fetch wrapper around the Fastify backend.
 *
 * - Injects Bearer token (resolved via authStore at call time).
 * - 401 → tries refresh once; on success, retries the original request.
 * - 401 even after refresh → forces logout (authStore will react).
 *
 * NOTE: ApiError carries the parsed body so consumers can show field-level errors
 * (vee-validate + zod shape).
 */
import { useAuthStore } from '@/stores/auth'
import { getApiBaseUrl } from '@/lib/server-url'
import type { ApiListResponse } from '@atendafilliados/shared'

export class ApiError extends Error {
  status: number
  body: unknown
  constructor(status: number, body: unknown, message: string) {
    super(message)
    this.status = status
    this.body = body
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

interface ApiOptions {
  method?: Method
  body?: unknown
  query?: Record<string, string | number | boolean | undefined>
  /** Set to true to skip the refresh-on-401 fallback (e.g. for /refresh itself). */
  noRetry?: boolean
  signal?: AbortSignal
  headers?: Record<string, string>
}

const FALLBACK_API_BASE = '/api'
// App nativo (Capacitor) usa a URL do servidor salva no aparelho ou a do
// build (VITE_API_URL); no navegador usa /api relativo (proxy do Vite/nginx).
if (getApiBaseUrl() === FALLBACK_API_BASE && import.meta.env.DEV) {
  // Avisa o dev uma vez: env não chegou ao bundle, e o proxy do Vite cobre
  // /api -> backend. Em produção um build sem VITE_API_URL quebra silencioso.
  // eslint-disable-next-line no-console
  console.warn(
    '[LupyBot] VITE_API_URL não está definido. Usando /api relativo ao origin atual (apenas OK em dev via Vite proxy). Configure .env antes de produção.',
  )
}
function buildUrl(path: string, query?: ApiOptions['query']): string {
  let url: URL
  if (/^https?:\/\//i.test(path)) {
    url = new URL(path)
  } else {
    const base = getApiBaseUrl().replace(/\/api\/?$/, '').replace(/\/+$/, '')
    const cleanPath = path.replace(/^\/+/, '')
    const normalizedPath = cleanPath.startsWith('api/') ? `/${cleanPath}` : `/api/${cleanPath}`
    url = new URL(`${base}${normalizedPath}`, window.location.origin)
  }
  if (query) {
    Object.entries(query).forEach(([k, v]) => {
      if (v !== undefined) url.searchParams.set(k, String(v))
    })
  }
  return url.toString()
}

async function rawRequest<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const auth = useAuthStore()
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...opts.headers,
  }
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json'
  if (auth.token) headers.Authorization = `Bearer ${auth.token}`

  const res = await fetch(buildUrl(path, opts.query), {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    credentials: 'include',
    signal: opts.signal,
  })
  const text = await res.text()
  const json = text ? safeParse(text) : null

  if (!res.ok) {
    const errBody = json as { message?: string } | null
    throw new ApiError(res.status, json, errBody?.message ?? `HTTP ${res.status}`)
  }
  return json as T
}

/** Public client — auto-attempts refresh on 401 (except when caller opts out). */
export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  try {
    return await rawRequest<T>(path, opts)
  } catch (err) {
    if (err instanceof ApiError && err.status === 401 && !opts.noRetry) {
      const auth = useAuthStore()
      const refreshed = await auth.refresh().catch(() => null)
      if (refreshed) return rawRequest<T>(path, opts)
      // `auth.refresh()` já limpa a sessão quando o servidor rejeita o
      // refresh. Em falhas transitórias de rede, preservamos a sessão e
      // deixamos o caller tentar novamente sem redirecionar para login.
      if (!auth.token && !auth.refreshToken) await auth.logout()
    }
    throw err
  }
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return { message: text }
  }
}

/**
 * Desempacota lista — aceita tanto `T[]` (flat, ex.: /api/logs) quanto
 * `ApiListResponse<T> = {data: T[]; meta: ...}` (paginado, ex.: /api/whatsapp/connections).
 * Retorna apenas o array de itens. Robusto a payloads malformados: devolve `[]`.
 */
export function unwrapList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[]
  if (payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown[] }).data)) {
    return ((payload as { data: T[] }).data) ?? []
  }
  return []
}

/** Convenience helpers. */
export const apiGet = <T>(path: string, query?: ApiOptions['query']) => api<T>(path, { query })
export const apiPost = <T>(path: string, body?: unknown, opts: Omit<ApiOptions, 'body' | 'method'> = {}) =>
  api<T>(path, { ...opts, method: 'POST', body })
export const apiPatch = <T>(path: string, body?: unknown, opts: Omit<ApiOptions, 'body' | 'method'> = {}) =>
  api<T>(path, { ...opts, method: 'PATCH', body })
export const apiPut = <T>(path: string, body?: unknown, opts: Omit<ApiOptions, 'body' | 'method'> = {}) =>
  api<T>(path, { ...opts, method: 'PUT', body })
export const apiDelete = <T>(path: string, opts: Omit<ApiOptions, 'method'> = {}) =>
  api<T>(path, { ...opts, method: 'DELETE' })

/** Download autenticado de ficheiros privados (backup, exportações). */
export async function apiDownload(path: string, query?: ApiOptions['query']): Promise<Blob> {
  const auth = useAuthStore()
  const request = () => fetch(buildUrl(path, query), {
    headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : {}, credentials: 'include',
  })
  let response = await request()
  if (response.status === 401 && await auth.refresh().catch(() => null)) response = await request()
  if (!response.ok) throw new ApiError(response.status, null, `HTTP ${response.status}`)
  return response.blob()
}
